import { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import Loader from "../../components/loader";
import DOCUMENTS from "../../api/services/DOCUMENTS";

// Numbering/setup configuration moved to the dedicated "הגדרות" tab
// (see /screens/documents/documentsSettings.jsx). This screen now focuses
// purely on listing/credit-noting issued documents.

// ─── Constants ──────────────────────────────────────────────────────────────────

const DOC_TYPE_LABELS = {
  305: "חשבונית מס-קבלה",
  400: "קבלה",
  330: "חשבונית זיכוי",
  320: "חשבונית מס",
};

const STATUS_LABELS = {
  reserved: "ממתין",
  issued: "פעיל",
  cancelled_by_credit: "בוטל בזיכוי",
  void: "בוטל",
};

const STATUS_COLORS = {
  reserved: "#F59E0B",
  issued: "#10B981",
  cancelled_by_credit: "#6B7280",
  void: "#9CA3AF",
};

// ─── Styles (inline — keep simple) ─────────────────────────────────────────────

const S = {
  page: { padding: 24, direction: "rtl", fontFamily: "Heebo, Arial, sans-serif", maxWidth: 1200, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
  headerActions: { display: "flex", gap: 8, alignItems: "center" },
  title: { fontSize: 28, fontWeight: 800, color: "#111827", margin: 0 },
  subtitle: { fontSize: 14, color: "#6B7280", marginTop: 4 },
  primaryBtn: { background: "#E93D67", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
  secondaryBtn: { background: "#F3F4F6", color: "#111827", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer", marginInlineStart: 6 },
  dangerBtn: { background: "#FEE2E2", color: "#991B1B", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer", marginInlineStart: 6 },
  iconBtn: { background: "#fff", color: "#374151", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, fontSize: 14, cursor: "pointer", marginInlineStart: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle" },
  iconBtnWA: { background: "#fff", color: "#25D366", border: "1px solid #E5E7EB", borderRadius: 8, width: 32, height: 32, fontSize: 16, cursor: "pointer", marginInlineStart: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", verticalAlign: "middle" },
  actionsCell: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0 },

  statsRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 24 },
  statCard: { background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: 16 },
  statLabel: { fontSize: 12, color: "#6B7280", marginBottom: 6 },
  statValue: { fontSize: 22, fontWeight: 800, color: "#111827" },

  filters: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" },
  // minWidth keeps the doc-type select wide enough to show the longest label
  // ("חשבונית מס-קבלה") without truncation at the start of the option text.
  select: { padding: "8px 32px 8px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, background: "#fff", minWidth: 180 },
  input: { padding: "8px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, minWidth: 200 },

  table: { width: "100%", borderCollapse: "collapse", background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #E5E7EB" },
  th: { padding: 12, textAlign: "right", fontSize: 12, fontWeight: 700, color: "#6B7280", background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" },
  td: { padding: 12, textAlign: "right", fontSize: 14, color: "#111827", borderBottom: "1px solid #F3F4F6" },
  // Child sub-row styling: muted bg + indented first cell
  trChild: { background: "#FAFAFA" },
  tdFirstChild: { paddingInlineStart: 36 },
  childArrow: { color: "#9CA3AF", marginInlineEnd: 4 },
  linkBadge: { display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, color: "#3730A3", background: "#E0E7FF", marginInlineStart: 6 },
  cancelledBadge: { display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, color: "#92400E", background: "#FEF3C7", marginInlineStart: 6 },
  emptyState: { padding: 60, textAlign: "center", color: "#6B7280" },

  badge: (color) => ({ display: "inline-block", padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700, color: "#fff", background: color }),
};

// ─── Helpers ────────────────────────────────────────────────────────────────────

/**
 * Flatten documents into parent/child rows so credit notes (330) appear
 * nested under the document they cancel.
 *
 * A doc is a child iff it has a linkedDocuments entry with role === "credits"
 * (it credits another doc). The parent is identified by that link's `id`.
 * If the parent is in the current page, the child sits indented below it.
 * Otherwise it stays at depth 0 and is flagged as `isOrphanChild` so the row
 * can show the parent's number via `linkedDocuments[0].originalNumber`.
 */
function groupDocs(docs) {
  if (!docs || docs.length === 0) return [];
  const idToDoc = new Map(docs.map((d) => [String(d._id), d]));
  const isChild = (d) => Array.isArray(d.linkedDocuments) && d.linkedDocuments.some((l) => l.role === "credits");
  const parentIdOf = (d) => {
    const link = d.linkedDocuments?.find((l) => l.role === "credits");
    return link ? String(link.id) : null;
  };

  const children = new Map(); // parentId → [child docs]
  const orphanChildren = []; // children whose parent isn't in this page
  const topLevel = [];

  for (const d of docs) {
    if (isChild(d)) {
      const pid = parentIdOf(d);
      if (pid && idToDoc.has(pid)) {
        if (!children.has(pid)) children.set(pid, []);
        children.get(pid).push(d);
      } else {
        orphanChildren.push(d);
      }
    } else {
      topLevel.push(d);
    }
  }

  const out = [];
  for (const p of topLevel) {
    out.push({ doc: p, depth: 0 });
    const kids = children.get(String(p._id)) || [];
    kids.sort((a, b) => b.number - a.number);
    for (const k of kids) out.push({ doc: k, depth: 1, parent: p });
  }
  for (const o of orphanChildren) out.push({ doc: o, depth: 0, isOrphanChild: true });
  return out;
}

// ─── Component ──────────────────────────────────────────────────────────────────

const DocumentsList = ({ onNewDoc }) => {
  const [list, setList] = useState(null);
  const [totals, setTotals] = useState({ count: 0, sumTotal: 0, sumVat: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ docType: "", q: "" });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await DOCUMENTS.list({ ...filters, limit: 50 });
      setList(res.items || []);
      setTotals(res.totals || { count: 0, sumTotal: 0, sumVat: 0 });
    } catch (e) {
      toast.error("טעינה נכשלה: " + (e?.response?.data?.message || e.message));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleViewPdf = async (id) => {
    try {
      const res = await DOCUMENTS.get_pdf(id);
      if (res?.url) window.open(res.url, "_blank", "noopener");
      else toast.error("לא נמצא קובץ PDF");
    } catch (e) {
      toast.error(e?.response?.data?.message || "לא ניתן להציג את המסמך");
    }
  };

  const handleCredit = async (doc) => {
    const reason = window.prompt("סיבת זיכוי:", "");
    if (!reason) return;
    try {
      const result = await DOCUMENTS.credit(doc._id, reason);
      toast.success(`חשבונית זיכוי מס׳ ${result.number} הופקה`);
      if (result.pdfUrl) window.open(result.pdfUrl, "_blank", "noopener");
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.message || "הפקת זיכוי נכשלה");
    }
  };

  // ─── Share helpers ─────────────────────────────────────────────────────────────
  // Backend now projects shortUrl on every document in the list response
  // (services/documents/nativeProvider.js list() maps items → {…d, shortUrl}).
  // Fall back to pdfUrl only if a doc somehow lacks a short slug.
  const docLink = (d) => d.shortUrl || d.pdfUrl || "";

  const handleCopyLink = async (d) => {
    const url = docLink(d);
    if (!url) return toast.error("אין קישור זמין");
    try {
      await navigator.clipboard.writeText(url);
      toast.success("הקישור הועתק");
    } catch {
      toast.error("העתקה נכשלה");
    }
  };

  const handleWhatsApp = (d) => {
    const url = docLink(d);
    if (!url) return toast.error("אין קישור זמין");
    const typeLabel = DOC_TYPE_LABELS[d.docType] || "מסמך";
    const customerName = d.customer?.name || "";
    const text = encodeURIComponent(
      `${typeLabel} מס׳ ${d.number}${customerName ? ` עבור ${customerName}` : ""}: ${url}`
    );
    // Normalise the Israeli phone to international form (972). If no phone
    // on file, open WhatsApp's general share so the user can pick.
    const rawPhone = (d.customer?.phone || "").replace(/\D/g, "");
    const intlPhone = rawPhone
      ? rawPhone.startsWith("972")
        ? rawPhone
        : "972" + rawPhone.replace(/^0/, "")
      : "";
    window.open(
      intlPhone
        ? `https://wa.me/${intlPhone}?text=${text}`
        : `https://wa.me/?text=${text}`,
      "_blank",
      "noopener"
    );
  };

  const handleEmail = (d) => {
    const url = docLink(d);
    if (!url) return toast.error("אין קישור זמין");
    const typeLabel = DOC_TYPE_LABELS[d.docType] || "מסמך";
    const subject = encodeURIComponent(`${typeLabel} מס׳ ${d.number}`);
    const body = encodeURIComponent(`לצפייה במסמך: ${url}`);
    const to = d.customer?.email ? encodeURIComponent(d.customer.email) : "";
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const fmt = (n) => "₪" + (Number(n) || 0).toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (d) => new Date(d).toLocaleDateString("he-IL");

  const rows = groupDocs(list);

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>חשבוניות ומסמכים</h1>
          <div style={S.subtitle}>הפק חשבוניות, קבלות ומסמכי זיכוי ישירות מתור.דיגיטל — מאושר על ידי רשות המסים</div>
        </div>
        <div style={S.headerActions}>
          <button style={S.primaryBtn} onClick={onNewDoc}>
            + הפק מסמך חדש
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        <div style={S.statCard}>
          <div style={S.statLabel}>סה״כ מסמכים</div>
          <div style={S.statValue}>{totals.count}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>סה״כ הכנסות</div>
          <div style={S.statValue}>{fmt(totals.sumTotal)}</div>
        </div>
        <div style={S.statCard}>
          <div style={S.statLabel}>סה״כ מע״מ שנגבה</div>
          <div style={S.statValue}>{fmt(totals.sumVat)}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={S.filters}>
        <select style={S.select} value={filters.docType} onChange={(e) => setFilters({ ...filters, docType: e.target.value })}>
          <option value="">כל סוגי המסמכים</option>
          {Object.entries(DOC_TYPE_LABELS).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </select>
        <input style={S.input} placeholder="חיפוש לפי שם לקוח או מספר" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        <button style={S.secondaryBtn} onClick={fetchList}>רענן</button>
      </div>

      {/* Table */}
      {loading ? <Loader /> : !list || list.length === 0 ? (
        <div style={{ ...S.table, padding: 0 }}>
          <div style={S.emptyState}>
            אין מסמכים עדיין. <br />
            <button style={{ ...S.primaryBtn, marginTop: 16 }} onClick={onNewDoc}>+ הפק את המסמך הראשון</button>
          </div>
        </div>
      ) : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>מס׳</th>
              <th style={S.th}>סוג</th>
              <th style={S.th}>תאריך</th>
              <th style={S.th}>לקוח</th>
              <th style={S.th}>סה״כ</th>
              <th style={S.th}>מע״מ</th>
              <th style={S.th}>סטטוס</th>
              <th style={S.th}>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ doc: d, depth, parent, isOrphanChild }) => {
              const isChild = depth === 1 || isOrphanChild;
              const rowStyle = isChild ? { ...S.trChild } : null;
              const firstCellStyle = isChild ? { ...S.td, ...S.tdFirstChild } : S.td;
              // Identify a "credited_by" link on a parent so we can badge the parent row.
              const creditedByLink = Array.isArray(d.linkedDocuments)
                ? d.linkedDocuments.find((l) => l.role === "credited_by")
                : null;
              // For orphan children, get original number from linkedDocuments[0].originalNumber.
              const orphanParentNumber = isOrphanChild
                ? d.linkedDocuments?.find((l) => l.role === "credits")?.originalNumber
                : null;
              return (
                <tr key={d._id} style={rowStyle}>
                  <td style={firstCellStyle}>
                    {isChild && <span style={S.childArrow}>↳</span>}
                    {d.number}
                  </td>
                  <td style={S.td}>
                    {DOC_TYPE_LABELS[d.docType] || d.docType}
                    {/* Child credit-note: show "credits #N" badge */}
                    {depth === 1 && parent && (
                      <span style={S.linkBadge}>מזכה את #{parent.number}</span>
                    )}
                    {isOrphanChild && orphanParentNumber && (
                      <span style={S.linkBadge}>מזכה את #{orphanParentNumber}</span>
                    )}
                    {/* Parent row that was cancelled: show "cancelled by credit #N" badge */}
                    {creditedByLink && (
                      <span style={S.cancelledBadge}>
                        בוטל בזיכוי #{creditedByLink.originalNumber}
                      </span>
                    )}
                  </td>
                  <td style={S.td}>{fmtDate(d.issuedAt || d.createdAt)}</td>
                  <td style={S.td}>{d.customer?.name || "—"}</td>
                  <td style={S.td}>{fmt(d.total)}</td>
                  <td style={S.td}>{fmt(d.vatAmount)}</td>
                  <td style={S.td}>
                    <span style={S.badge(STATUS_COLORS[d.status] || "#9CA3AF")}>
                      {STATUS_LABELS[d.status] || d.status}
                    </span>
                  </td>
                  <td style={S.td}>
                    <div style={S.actionsCell}>
                      <button style={S.secondaryBtn} onClick={() => handleViewPdf(d._id)}>צפה ב-PDF</button>
                      <button
                        style={S.iconBtn}
                        onClick={() => handleCopyLink(d)}
                        title="העתק קישור">📋</button>
                      <button
                        style={S.iconBtnWA}
                        onClick={() => handleWhatsApp(d)}
                        title={d.customer?.phone ? `שלח בוואטסאפ ל-${d.customer.phone}` : "שלח בוואטסאפ"}>💬</button>
                      <button
                        style={S.iconBtn}
                        onClick={() => handleEmail(d)}
                        title={d.customer?.email ? `שלח באימייל ל-${d.customer.email}` : "שלח באימייל"}>✉️</button>
                      {/* Hide "issue credit" on credit notes themselves and on already-cancelled docs */}
                      {d.status === "issued" && d.docType !== 330 && (
                        <button style={S.dangerBtn} onClick={() => handleCredit(d)}>הפק זיכוי</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

    </div>
  );
};

export default DocumentsList;
